import { BrowserRouter, Navigate, NavLink, Outlet, Route, Routes, useLocation } from 'react-router-dom'
import { lazy, Suspense, useEffect, type ReactNode } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Menu, X } from 'lucide-react'
import { useState } from 'react'

import { AnimatedBackground } from '@/components/AnimatedBackground'
import { getAdminSession, clearAdminSession } from '@/lib/adminSession'
import { publicApi, systemApi } from '@/lib/api'
import { getFallbackSiteSettings, readBootSiteSettings, readCachedSiteSettings, writeCachedSiteSettings } from '@/lib/siteSettingsCache'
import { HomePage } from '@/pages/HomePage'
import { OrdersQueryPage } from '@/pages/OrdersQueryPage'
import { ProductCatalogPage } from '@/pages/ProductCatalogPage'

const AdminCardPoolsPage = lazy(() =>
  import('@/pages/AdminCardPoolsPage').then(module => ({ default: module.AdminCardPoolsPage }))
)
const AdminDashboardPage = lazy(() =>
  import('@/pages/AdminDashboardPage').then(module => ({ default: module.AdminDashboardPage }))
)
const AdminLoginPage = lazy(() => import('@/pages/AdminLoginPage').then(module => ({ default: module.AdminLoginPage })))
const AdminOrdersPage = lazy(() => import('@/pages/AdminOrdersPage').then(module => ({ default: module.AdminOrdersPage })))
const AdminPersonalizationPage = lazy(() =>
  import('@/pages/AdminPersonalizationPage').then(module => ({ default: module.AdminPersonalizationPage }))
)
const AdminProductsPage = lazy(() =>
  import('@/pages/AdminProductsPage').then(module => ({ default: module.AdminProductsPage }))
)
const AdminSettingsPage = lazy(() => import('@/pages/AdminSettingsPage').then(module => ({ default: module.AdminSettingsPage })))
const InstallPage = lazy(() => import('@/pages/InstallPage').then(module => ({ default: module.InstallPage })))
const OrderDetailPage = lazy(() => import('@/pages/OrderDetailPage').then(module => ({ default: module.OrderDetailPage })))
const PaymentPreviewPage = lazy(() =>
  import('@/pages/PaymentPreviewPage').then(module => ({ default: module.PaymentPreviewPage }))
)
const ProductPage = lazy(() => import('@/pages/ProductPage').then(module => ({ default: module.ProductPage })))

function withDeferredPage(content: ReactNode) {
  return <Suspense fallback={null}>{content}</Suspense>
}

function PublicLayout() {
  const location = useLocation()
  const isHomePage = location.pathname === '/'
  const cachedSiteSettings = readCachedSiteSettings() ?? readBootSiteSettings()
  const siteSettingsQuery = useQuery({
    queryKey: ['public-site-settings'],
    queryFn: publicApi.getSiteSettings,
    initialData: cachedSiteSettings ?? undefined,
  })
  const siteSettings = siteSettingsQuery.data ?? cachedSiteSettings ?? getFallbackSiteSettings()

  useEffect(() => {
    document.title = siteSettings.pageTitle
  }, [siteSettings.pageTitle])

  useEffect(() => {
    document.documentElement.dataset.theme = siteSettings.themePreset
  }, [siteSettings.themePreset])

  useEffect(() => {
    if (siteSettingsQuery.data) {
      writeCachedSiteSettings(siteSettingsQuery.data)
    }
  }, [siteSettingsQuery.data])

  return (
    <div className="app-shell">
      <AnimatedBackground />
      <main className={`main-container ${isHomePage ? 'main-container--home main-container--split-home' : ''}`}>
        <Outlet />
      </main>
      {!isHomePage ? (
        <footer className="site-footer">
          <div className="site-footer__inner">
            <span>&copy; {siteSettings.footerYear} {siteSettings.brandName}. All rights reserved.</span>
            {siteSettings.beianNo ? (
              <a className="site-footer__beian" href="https://beian.miit.gov.cn/" rel="noreferrer" target="_blank">
                {siteSettings.beianNo}
              </a>
            ) : null}
          </div>
        </footer>
      ) : null}
    </div>
  )
}

function RequireAdmin() {
  const session = getAdminSession()
  const location = useLocation()

  if (!session) {
    return <Navigate to="/admin/login" replace state={{ from: location.pathname }} />
  }

  return <Outlet />
}

function RequireInstalled({ shouldRedirectToInstall }: { shouldRedirectToInstall: boolean }) {
  const location = useLocation()

  if (shouldRedirectToInstall) {
    return <Navigate to="/install" replace state={{ from: location.pathname }} />
  }

  return <Outlet />
}

function AdminLayout() {
  const location = useLocation()
  const [menuOpen, setMenuOpen] = useState(false)
  const cachedSiteSettings = readCachedSiteSettings() ?? readBootSiteSettings()
  const siteSettingsQuery = useQuery({
    queryKey: ['public-site-settings'],
    queryFn: publicApi.getSiteSettings,
    initialData: cachedSiteSettings ?? undefined,
  })
  const siteSettings = siteSettingsQuery.data ?? cachedSiteSettings ?? getFallbackSiteSettings()
  useEffect(() => {
    document.title = siteSettings.pageTitle
  }, [siteSettings.pageTitle])
  useEffect(() => {
    document.documentElement.dataset.theme = siteSettings.themePreset
  }, [siteSettings.themePreset])
  useEffect(() => {
    if (siteSettingsQuery.data) {
      writeCachedSiteSettings(siteSettingsQuery.data)
    }
  }, [siteSettingsQuery.data])
  useEffect(() => {
    setMenuOpen(false)
  }, [location.pathname])
  const links = [
    { to: '/admin', label: '仪表板' },
    { to: '/admin/products', label: '商品' },
    { to: '/admin/orders', label: '订单' },
    { to: '/admin/card-pools', label: '卡池' },
    { to: '/admin/personalization', label: '个性化' },
    { to: '/admin/settings', label: '系统设置' },
  ]

  return (
    <div className="admin-shell">
      <aside className="admin-sidebar">
        <div className="admin-sidebar__top">
          <div>
            <p className="admin-sidebar__eyebrow">{siteSettings.brandName} Admin</p>
            <h1>控制台</h1>
          </div>
          <button
            aria-expanded={menuOpen}
            aria-label={menuOpen ? '关闭菜单' : '打开菜单'}
            className="menu-toggle"
            onClick={() => setMenuOpen(current => !current)}
            type="button"
          >
            {menuOpen ? <X size={18} /> : <Menu size={18} />}
          </button>
        </div>
        <div className={`admin-sidebar__menu ${menuOpen ? 'is-open' : ''}`}>
          <nav className="admin-sidebar__nav">
            {links.map(link => (
              <NavLink key={link.to} className={location.pathname === link.to ? 'is-active' : ''} to={link.to} end>
                {link.label}
              </NavLink>
            ))}
          </nav>
          <div className="admin-sidebar__footer">
            <button
              className="ghost-button"
              onClick={() => {
                clearAdminSession()
                window.location.href = '/admin/login'
              }}
              type="button"
            >
              退出登录
            </button>
            <NavLink className="ghost-button" to="/">
              返回首页
            </NavLink>
          </div>
        </div>
      </aside>
      <section className="admin-content">
        <Outlet />
      </section>
    </div>
  )
}

export function AppRouter() {
  return (
    <BrowserRouter>
      <InstallAwareRoutes />
    </BrowserRouter>
  )
}

function InstallAwareRoutes() {
  const installStatusQuery = useQuery({
    queryKey: ['install-status'],
    queryFn: systemApi.getInstallStatus,
    retry: false,
  })

  const shouldRedirectToInstall = installStatusQuery.isSuccess && installStatusQuery.data.installed === false

  return (
    <Routes>
      <Route
        path="/install"
        element={shouldRedirectToInstall ? withDeferredPage(<InstallPage />) : <Navigate replace to="/" />}
      />
      <Route element={<RequireInstalled shouldRedirectToInstall={shouldRedirectToInstall} />}>
        <Route element={<PublicLayout />}>
          <Route path="/" element={<HomePage />} />
          <Route path="/products" element={<ProductCatalogPage />} />
          <Route path="/products/:slug" element={withDeferredPage(<ProductPage />)} />
          <Route path="/orders/query" element={<OrdersQueryPage />} />
          <Route path="/orders/:orderNo" element={withDeferredPage(<OrderDetailPage />)} />
          <Route path="/payment-preview/alipay" element={withDeferredPage(<PaymentPreviewPage />)} />
        </Route>
        <Route path="/admin/login" element={withDeferredPage(<AdminLoginPage />)} />
        <Route element={<RequireAdmin />}>
          <Route path="/admin" element={<AdminLayout />}>
            <Route index element={withDeferredPage(<AdminDashboardPage />)} />
            <Route path="products" element={withDeferredPage(<AdminProductsPage />)} />
            <Route path="orders" element={withDeferredPage(<AdminOrdersPage />)} />
            <Route path="card-pools" element={withDeferredPage(<AdminCardPoolsPage />)} />
            <Route path="personalization" element={withDeferredPage(<AdminPersonalizationPage />)} />
            <Route path="settings" element={withDeferredPage(<AdminSettingsPage />)} />
          </Route>
        </Route>
      </Route>
      <Route path="*" element={<Navigate replace to={shouldRedirectToInstall ? '/install' : '/'} />} />
    </Routes>
  )
}
